<template>
  <div class="tw:mx-4 tw:lg:mx-0">
    <div class="tw:my-4">
      <CardLoadingSkeleton v-if="isAccommodationBasicInfoLoading" height="202px" />
      <PropertyBasicInfo v-else :accommodationInfo="accommodationBasicInfo" />
    </div>
    <div class="tw:my-4">
      <CardLoadingSkeleton v-if="isAccommodationBasicInfoLoading || isPopularFiltersLoading" height="202px" />
      <PropertyPopularFilters
        v-else
        :allPopularFilters="availablePopularFilters"
        :popularFilters="popularFilters"
        :currentFilters="accommodationFeatures" />
    </div>
    <div class="tw:my-4">
      <CardLoadingSkeleton v-if="accommodationFeaturesLoading" height="1060px" />
      <PropertyFeatures v-else :accommodationFeatures="accommodationFeatures" />
    </div>
    <div class="tw:my-4">
      <CardLoadingSkeleton v-if="isLanguageFieldsLoading" height="202px" />
      <PropertyLanguages v-else />
    </div>
  </div>
  <FullScreenLoader
    v-show="isAccommodationBasicInfoFetching || isLanguageFieldsLoading || accommodationFeaturesLoading" />
</template>

<script setup lang="ts">
import { computed, onMounted, type ComputedRef } from 'vue'
import { useRouter, onBeforeRouteUpdate } from 'vue-router'

import PropertyBasicInfo from '@/components/propertyDetails/propertyBasicInfo/PropertyBasicInfo.vue'
import PropertyFeatures from '@/components/propertyDetails/propertyFeatures/PropertyFeatures.vue'
import PropertyLanguages from '@/components/propertyDetails/propertyLanguages/PropertyLanguages.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'
import CardLoadingSkeleton from '@/components/loadingSkeletons/CardLoadingSkeleton.vue'
import PropertyPopularFilters from '@/components/propertyDetails/propertyPopularFilters/PropertyPopularFilters.vue'

import { useAccommodationBasicInfo, usePropertyFeatures } from '@/components/propertyDetails/queries'
import { useLanguageFields } from '@/components/propertyDetails/propertyLanguages/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { popularFiltersIds } from '@/constants/propertyDetailsPopularFilters'
import { useGetPopularFilters } from '@/components/propertyDetails/queries'

import type { PropertyFeature, UIFeatureGroup } from '@/types/propertyDetails'

const router = useRouter()

const { allPopularFilters, isPopularFiltersLoading } = useGetPopularFilters()
const { isLanguageFieldsLoading } = useLanguageFields(selectedAccommodationId)
const { isAccommodationBasicInfoLoading, isAccommodationBasicInfoFetching, accommodationBasicInfo } =
  useAccommodationBasicInfo()
const { accommodationFeaturesLoading, accommodationFeatures } = usePropertyFeatures()

/**
 * Filter accommodation features to show only popular filters
 */
const popularFilters: ComputedRef<PropertyFeature[]> = computed(() => {
  if (accommodationFeaturesLoading.value || isAccommodationBasicInfoLoading.value) return []

  const filteredValues = accommodationFeatures.value?.reduce<PropertyFeature[]>((acc, obj: UIFeatureGroup) => {
    const matchingValues =
      obj?.fields === null
        ? []
        : (obj.fields?.filter((value: PropertyFeature) =>
            (popularFiltersIds as readonly number[]).includes(value.featureId)
          ) ?? [])

    return acc.concat(matchingValues)
  }, [])

  return filteredValues?.filter((item: PropertyFeature) => item.featureId) ?? []
})

/**
 * Filter all available popular filters
 */
const availablePopularFilters: ComputedRef<PropertyFeature[]> = computed(() => {
  if (isPopularFiltersLoading.value || isAccommodationBasicInfoLoading.value) return []

  const filteredValues = allPopularFilters.value?.filter((item: PropertyFeature) =>
    (popularFiltersIds as readonly number[]).includes(item.featureId)
  )

  return filteredValues?.filter((item: PropertyFeature) => item.featureId) ?? []
})

/**
 * Prevent direct navigation to /property-details root
 * Auto-redirects to basic-information tab
 */
onBeforeRouteUpdate((to, from) => {
  // Prevent navigation if already in property-details section
  if (from.path.includes('/property-details') && to.path === '/property-details') {
    return false
  }

  // Redirect to basic information tab
  if (to.path === '/property-details') {
    return '/property-details/basic-information'
  }
})

/**
 * On mount, ensure we're on a valid tab
 * Redirects to basic-information if on root route
 */
onMounted(() => {
  if (router.currentRoute.value.name === 'property-details') {
    router.push('/property-details/basic-information')
  }
})
</script>
